import {compact} from "lodash";
import {useCallback, useMemo} from "react";
import {checkIfSameDay} from "src/utils/timeUtils";

import {Address, LocationSpecialty} from "../_services/types";
import {
  ApptReasonFragment,
  LocSlotPair,
  SlotParams,
  maybePairLocsWithSlots,
} from "../components/v5/Locations/usePairLocsWithSlots";
import {getAllowedLocations} from "../constants/locations";
import {useQueryController} from "../hooks/useQueryController";
import {addDistToLocsByPoint} from "../hooks/useSortLocations";
import {Coordinate} from "../store/types";
import {getGeolocation} from "../utils/browser-storage/location";
import {fetchLocationsAndPractices} from "../utils/fetchLocationsAndPractices";
import {fetchPaginatedLocationIdsForApptReason} from "../utils/fetchLocationsByReasonId";
import {hasLimitedAvailability} from "../utils/limitedAvailability";

export type LocationFragment = {
  id: string;
  name: string;
  slug: string;
  practiceId: string;
  address: Address;
  googlePlaceId?: string;
  specialtyIds: string[];
  timezone: string;
  dist?: number;
  coverImageId: string;
  locationSpecialties: LocationSpecialty[];
};

const MAX_DISTANCE = 30;
const MAX_NEARBY_LOCATION_COUNT = 3;

const getLocsWithinDist = <T extends {address: Address}>(
  locs: T[],
  coordinate: Coordinate,
  dist = MAX_DISTANCE,
) => locs.map(loc => addDistToLocsByPoint(loc, coordinate, 1)).filter(l => l.dist < dist);

export const fetchNearbyLocations = async (
  practiceApptReasonId: string,
  position: Coordinate,
  count: number,
) => {
  const [{locations}, ids] = await Promise.all([
    fetchLocationsAndPractices(),
    fetchPaginatedLocationIdsForApptReason({
      reasonId: practiceApptReasonId,
      latitude: position.x,
      longitude: position.y,
    }),
  ]);
  // n^2 is not optimal here but the lists shouldn't be long enough to matter too much
  const matchingLocations = compact(ids.map(id => locations.find(loc => loc.id === id)));
  return getLocsWithinDist(getAllowedLocations(matchingLocations), position).slice(0, count);
};

const fetchSlotsInArea = async (
  initialLocation: LocationFragment,
  apptReason: ApptReasonFragment,
  slotParams: SlotParams,
  disableNearbySlots: boolean,
): Promise<{
  initialLocation: LocSlotPair<LocationFragment>;
  nearbyLocations: LocSlotPair<LocationFragment>[];
  availabilityIsLimited?: boolean;
}> => {
  const ignore2HrAvailabilityRule = slotParams.from
    ? !checkIfSameDay(slotParams.from, Date.now(), slotParams.timezone)
    : false;

  const [initialResponse] = await maybePairLocsWithSlots([initialLocation], apptReason, slotParams);

  const availabilityIsLimited = hasLimitedAvailability(
    initialResponse.slots,
    ignore2HrAvailabilityRule,
  );

  if (!availabilityIsLimited || disableNearbySlots)
    return {
      initialLocation: initialResponse,
      nearbyLocations: [],
      availabilityIsLimited,
    };

  const initialLocationPosition = {
    x: initialLocation.address.latitude,
    y: initialLocation.address.longitude,
  };

  const nearbyLocations = await fetchNearbyLocations(apptReason.id, initialLocationPosition, 10);
  const nearbyLocationsWithSlots = await maybePairLocsWithSlots(
    nearbyLocations,
    apptReason,
    slotParams,
  ).then(results =>
    results
      // This fixes a type incompatibility issue between `RootStateLocation` and `LocationFragment`
      .map(item => ({
        ...item,
        location: {
          ...item.location,
          locationSpecialties: item.location.locationSpecialties || [],
        },
      }))
      .filter(item => item.soonestSlot && item.location.id !== initialLocation.id)
      .slice(0, MAX_NEARBY_LOCATION_COUNT),
  );

  const initialLocationWithDistance = {
    ...initialResponse,
    location: addDistToLocsByPoint(initialResponse.location, initialLocationPosition),
  };

  return {
    initialLocation: initialLocationWithDistance,
    nearbyLocations: nearbyLocationsWithSlots,
    availabilityIsLimited,
  };
};

export const useFetchSlotsInArea = (
  initialLocation: LocationFragment,
  apptReason: ApptReasonFragment,
  slotParams: SlotParams,
  disableNearby: boolean,
) => {
  const defaultValue: Awaited<ReturnType<typeof fetchSlotsInArea>> = useMemo(
    () => ({
      initialLocation: {
        location: initialLocation,
        specialtyId: undefined,
        soonestSlot: null,
        slots: [],
      },
      nearbyLocations: [],
      availabilityIsLimited: undefined,
    }),
    [initialLocation],
  );

  const fn = useCallback(
    () => fetchSlotsInArea(initialLocation, apptReason, slotParams, disableNearby),
    [initialLocation, apptReason, slotParams, disableNearby],
  );

  return useQueryController({
    fn,
    initialValue: defaultValue,
    cacheKey: JSON.stringify({
      userPosition: getGeolocation(),
      initialLocation,
      apptReason,
      slotParams,
    }),
  });
};
